import { css } from '@emotion/react';
import { createTheme, } from '@mui/material';
import styled from "@emotion/styled";
import  managePlatformBottomBg from "../images/managePlatformBottomBg.svg";


/* Supports weights 100-900 */
import '@fontsource/poppins';

// Supports weights 200-800
import '@fontsource-variable/karla';
import { Height } from '@mui/icons-material';

/* custom colors */
export const colors = {

  primaryMain: '#40B449',  // Kelly Green
  primaryLight: '#C4D82E', // Bright lime
  primaryDark: '#14294E',  // Blue
  primaryContrastText: '#FFFFFF',  
  primaryMainSelected :'#FFFFFF',

  secondaryMain: '#C4D82E', // Bright lime
  secondaryLight: '#8AC440', // Lime
  secondaryDark: '#44C4F2',  // Bright Blue
  secondaryContrastText: '#08162E',  
  
  primaryText: 'rgba(0, 0, 0, 0.87)', 
  secondaryText: 'rgba(0, 0, 0, 0.60)',  
  DisabledText: 'rgba(0, 0, 0, 0.38)',  
  
  errorMain: '#D32F2F', //red
  errorLight: '#FF5722',
  errorDark: '#BF360D',
  errorContrastText: '#FFFFFF',

  successMain: '#2F7D31', 
  successLight: '#4CAF50',
  successDark: '#1A5E20',
  successContrastText: '#FFFFFF',
  
  white: '#FFFFFF',
  lightWhite: 'rgba(255, 255, 255, 0.87)',
  darkLightWhite: '#F2F2F2',  
  darkierWhite: 'rgba(255, 255, 255, 0.42)', 
  darkestWhite: 'rgba(255, 255, 255, 0.6)',


  black: '#000000',
  lightBlack: 'rgba(0, 0, 0, 0.56)',
  gray: '#E7E7E7',  
  lightGray: '#F0F0F0',  
  lightierGray: '#D9D9D9',
  darkGray: '#BDBDBD', 
  darkestGray: '#979ea8',
  borderColor: 'rgba(0, 0, 0, 0.12)',
    
};


export const fontFamily = {
  poppins: "'Poppins', sans-serif;", 
  karla: "'Karla Variable', sans-serif;",
}

/* pixel to rem */
export const pxtorem = (size: number) => `${size / 16}rem`;

/* Media queries */
const breakpoints = [767, 992, 1200, 1536];
export const mq = breakpoints.map(
  (bp) => `@media only screen and(min - width: ${bp}px)`
);

export const device = {
  xs: '536px',
  sm: '767px',
  smin: '768px',
  md: '992px',
  mmd: '1024px',
  lg: '1200px',
  xl: '1536px',
  xxl: '1920px',
  xxxl: '2160px',
}

export const media = {
  xs: (...args: any) => css`
    @media (max-width: ${device.xs}) {
      ${css(...args)};
    }
  `,
  sm: (...args: any) => css`
    @media (max-width: ${device.sm}) {
      ${css(...args)};
    }
  `,
  smin: (...args: any) => css`
  @media (min-width: ${device.smin}) {
    ${css(...args)};
  }
 `,
  md: (...args: any) => css`
    @media (max-width: ${device.md}) {
      ${css(...args)};
    }
  `,
  mmd: (...args: any) => css`
    @media (max-width: ${device.mmd}) {
      ${css(...args)};
    }
  `,
  lg: (...args: any) => css`
    @media (max-width: ${device.lg}) {
      ${css(...args)};
    }
  `,
  // xl: (...args: any) => css`
  //   @media (max-width: ${device.xl}) {
  //     ${css(...args)};
  //   }
  // `,
  xxl: (...args: any) => css`
    @media (min-width: ${device.xxl}) {
      ${css(...args)};
    }
  `,
   xxxl: (...args: any) => css`
   @media (min-width: ${device.xxxl}) {
     ${css(...args)};
   }
 `,
}

export const palettePrimary = {
  main: colors.primaryMain,
  light: colors.primaryLight,
  dark: colors.primaryDark,
  contrastText: colors.primaryContrastText,
}

export const paletteSecondary = {
  main: colors.secondaryMain,
  light: colors.secondaryLight,
  dark: colors.secondaryDark,
  contrastText: colors.secondaryContrastText,
}

export const paletteText = {
  primary: colors.primaryText,
  secondary: colors.secondaryText,
  disabled: colors.DisabledText,
  hint: colors.black,
}
export const paletteError = {
  main: colors.errorMain,
  light: colors.errorLight,
  dark: colors.errorDark,
  contrastText: colors.errorContrastText,
}

export const paletteSuccess = {
  main: colors.successMain,
  light: colors.successLight,
  dark: colors.successDark,
  contrastText: colors.successContrastText,
}

/* Theme style start*/
export const theme = createTheme({

  palette: {
    mode: 'light',
    primary: palettePrimary,
    secondary: paletteSecondary,
    text: paletteText,      
    error: paletteError,
    success: paletteSuccess,   
  },

  typography: {
    fontFamily: fontFamily.karla,

    h1: {      
      fontSize: pxtorem(25),    
      fontFamily: fontFamily.poppins,
      fontWeight: 600,
      lineHeight: '1.5',
      letterSpacing: '0',
      color: colors.primaryMain,
      margin: '0 0 15px 0',
      '@media (max-width:767px)': {
        
      },      
    },
    
    h2: {      
      fontSize: pxtorem(16),    
      fontFamily: fontFamily.poppins,
      fontWeight: 700,
      lineHeight: '1.5',
      letterSpacing: '0',
      color: colors.primaryMain,
      margin: '0 0 15px 0',
      '@media (max-width:767px)': {
        
      },      
    },

    h3: {
      fontSize: pxtorem(16),
      fontFamily: fontFamily.karla,
      color: colors.primaryText,
      fontWeight: 700,
      lineHeight: '1.16',
      letterSpacing: '0',
      margin: '0 0 20px 0',
    },  
    body1: {
      fontSize: pxtorem(14),
      lineHeight: '1.5',
      fontWeight: '400',
      color: colors.primaryText,
    },
    body2: {
      fontSize: pxtorem(15),
      fontWeight: '400',
      lineHeight: '1.42',
      color: colors.primaryText,
    },
  },

  shape: {
    borderRadius: 6,
  },
  components: {

    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: pxtorem(13),         
          lineHeight: '1.16',
          fontWeight: 700,
          boxShadow: 'none',
          padding: '5px 15px 4px 15px',
          height: '35px',
          borderRadius: '25px',
          textTransform: 'uppercase',
          outline: 'none !important',
          color: colors.secondaryContrastText,   
          '&:focus': {
            outline: 'none !important',
          },
          variants: [
            {
              props: { variant: 'contained' },
              style: {
                backgroundColor: colors.secondaryLight,
                '&:hover': {
                  backgroundColor: colors.secondaryDark,
                },
              },
            },
            {
              props: { variant: 'outlined' },
              style: {
                borderColor: colors.secondaryContrastText,                
                '&:hover': {
                  borderColor: colors.primaryMain,
                  color: colors.primaryMain,
                },
              },
            },
          ],          
        },
        outlinedError: {      
          borderColor:  colors.errorMain,
          color: colors.errorMain,
          "&:hover": {
            borderColor: colors.errorMain,
            color: colors.errorMain,
          },
        },
       
      },    
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          '&.pending': {
            backgroundColor: 'rgba(68, 196, 242, 0.3)',
          },
          '&.hold': {
            backgroundColor: 'rgba(255, 152, 0, 0.3)',
          },
          '&.terminated': {
            backgroundColor: 'rgba(239, 83, 80, 0.3)',
          },
          '&.inactive': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
          '&.active': {
            backgroundColor: 'rgba(132, 196, 64, 0.3)',
          },
        },
        sizeSmall: {
        }
      },     
    },
    
 
    MuiTextField: {
      defaultProps: {
        variant: "outlined", 
      },
      styleOverrides: {
        root: {        
          width: '100%', 
          marginBottom: '5px',
          "& .MuiInputLabel-root": {
            top: pxtorem(-6),   
            color: colors.secondaryText,  
            fontSize: pxtorem(13),  
            
          },           
          "& .MuiInputLabel-root[data-shrink='true']": {
            top: "0",       
            fontSize: pxtorem(11),      
            transform: 'translate(14px, -9px) scale(1)',
            backgroundColor: colors.white,
            paddingRight: '5px',
            
          },  
          "& .MuiOutlinedInput-root": {
            height: "40px",
            "& fieldset": {
              // Default border color
            },
            "&:hover fieldset": {
              // Border on hover
              // borderColor: colors.primaryDark,            
              },
          
            "&.MuiInputBase-multiline": {
              height: 'auto !important',
            },
          },
          "& .MuiFormHelperText-root": {
            marginTop: '0px',
            fontSize: pxtorem(11), 
          },      
        },
      },
    },  

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          boxShadow: "none",
          borderBottom: "solid 1px rgba(0,0,0,0.12)",
          borderRadius: 0,          
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {          
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: 0,
          minHeight: "80px",        
          "&.Mui-expanded": {           
           padding: 0,           
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
  }, 
})

export default theme


/* scrollBar */
export const customScroll = css`
  &::-webkit-scrollbar-track {
    border-radius: 50px;
    background-color: ${colors.white};
  
  }
  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
    border-radius: 50px;
    background-color: rgba(211, 211, 211, 1);   
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 50px;
    background-color: rgba(211, 211, 211, 1);
  }

  /* Firefox */
 // scrollbar-width: thin;
`;

export const hvCenter = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

/* Global styles */
export const globalStyles = css`

  /* poppins-latin-600-normal */
  @font-face {
    font-family: 'Poppins';
    font-style: normal;
    font-display: swap;
    font-weight: 600;
    src: url(https://cdn.jsdelivr.net/fontsource/fonts/poppins@latest/latin-600-normal.woff2) format('woff2'), 
    url(https://cdn.jsdelivr.net/fontsource/fonts/poppins@latest/latin-600-normal.woff) format('woff');
    /* src: url('../fonts/poppins-latin-600-normal.woff2') format('woff2'), 
    url('../fonts/poppins-latin-600-normal.woff') format('woff');  */
    unicode-range: U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD;
  }

  /* poppins-latin-700-normal */
  @font-face {
    font-family: 'Poppins';
    font-style: normal;
    font-display: swap;
    font-weight: 700;
    src: url(https://cdn.jsdelivr.net/fontsource/fonts/poppins@latest/latin-700-normal.woff2) format('woff2'), 
    url(https://cdn.jsdelivr.net/fontsource/fonts/poppins@latest/latin-700-normal.woff) format('woff');
    /* src: url('../fonts/poppins-latin-700-normal.woff2') format('woff2'), 
    url('../fonts/poppins-latin-700-normal.woff') format('woff'); */
    unicode-range: U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD;
  }

  * {
    margin: 0;
  }
  *, *::before, *::after {
    box-sizing: border-box;
  }
  #root {
    height: 100%;
  }
  html {
    height: 100%;
    font-size: 16px !important;  
    @media screen and (min-width: 1920px) {
      /* font-size: 24px !important;   */
    }
    @media screen and (min-width: 2160px) {
      font-size: 22px !important;  
    }
    @media screen and (min-width: 2560px) {
      /* font-size: 20px !important;        */
    }
  }  
  body {
    height: 100%;
    margin: 0;
    padding: 60px 0 0 0;    
    font-size : ${pxtorem(16)};
    font-family: ${fontFamily.karla};     
    color:  ${colors.primaryText};   
    background-color:${colors.lightGray};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;  
    
  }
  a {
    
  }
  a:hover {
   cursor: pointer;   
  }
  .main-container{
    height: 100%;
  }
  
  input[type="search"]::-webkit-search-decoration,
  input[type="search"]::-webkit-search-cancel-button,
  input[type="search"]::-webkit-search-results-button,
  input[type="search"]::-webkit-search-results-decoration { 
    display: none; 
  }

 /* utility style start */
  .d-flex {
    display: flex !important;
  }
  .justify-content-center {
    justify-content: center !important;
  }
  .align-items-center {
    align-items: center !important;
  }
  .justify-content-between {
    justify-content: space-between !important;
  }
  .justify-content-start {
    justify-content: flex-start !important;
  }
  .justify-content-end {
    justify-content: flex-end !important;
  }
  .align-items-start {
    align-items: flex-start !important;
  }
  .align-items-end {
    align-items: flex-end !important;
  }

  .flex-direction-column{
    flex-direction: column !important;
  }
  
  .Position-relative{
    position: relative;
  }
  .tt-uppercase {
    text-transform: uppercase;
  }
  .fw-600 {
    font-weight: 600 !important;
  }
  .text-red {
    color: ${colors.errorMain};
  }
  .text-blue {
    color: ${colors.secondaryContrastText} !important;
  }
  .bg-white{
    background-color: ${colors.white} !important;
   &:hover td {
    background-color: ${colors.white} !important;
   }
  }
  .break-word {
    white-space: normal;
    word-wrap: break-word;    
    word-break: break-all;
  }
  
  .underLine, .MuiLink-root {
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  
  .fs-13 {
    font-size: ${pxtorem(13)} !important;
  }
  .fs-16 {
    font-size: ${pxtorem(16)} !important;
  }
  .fs-17 {
    font-size: ${pxtorem(17)} !important;
  }
  .mr-10 {
    margin-right: 10px;
  }
  .mb-10 {
    margin-bottom: 10px !important;
  }
  .mb-20 {
    margin-bottom: 20px !important;
  }
  
  .hvCenter {
    ${hvCenter};
  }
  .text-ellipsis {
    display: block;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
    &.max-width-350 {
      max-width: 350px;
    }
  }

  .centerAlignLoader {
    position: absolute;
    left: 50%;
    top: 50%;
    width: ${pxtorem(48)};
    height: ${pxtorem(48)};
    transform: translate(-50%, -50%);
  }

  /* utility style end */
  
  
  .link-splash {
    border-radius: 25px;
    box-shadow: 0px 0px 40px 0px rgba(255,255,255,0.6);
    background: linear-gradient(to bottom,  rgba(196,216,46,1) 0%,rgba(64,180,73,1) 100%);
    
    display: inline-block;
    padding: 13px 10px !important;
    min-width: 138px;
    height: 48px;
    text-decoration: none;
    text-align: center;

    font-weight: 700;
    font-size: ${pxtorem(16)};
    line-height: 1.5;
    text-transform: uppercase;
    color: ${colors.secondaryContrastText};    
    &:hover {
      background: ${colors.secondaryDark};
    }
    .MuiCircularProgress-root{
      margin: 3px 0 0 7px;
      position: absolute;
      color: ${colors.white};
    }
  }
  
 .icon-sensitive {
   width: 30px;
   height: 30px;
   margin-right: 3px;
 }

 .iconBtn{
  width: 30px;
  height: 30px;
 }
 .recentDot {
    width: 8px;
    height: 8px;
    border-radius: 100%;
    display: inline-block;
    margin: 0 3px 0 0;
    background: ${colors.primaryMain};
  }

 #upload-file-button {
      .MuiMenuItem-root{
          font-size: ${pxtorem(16)};
          letter-spacing: .15px;
      }
  }

  .btnBack {
      background: ${colors.primaryMain};
      color: ${colors.white};
      width: ${pxtorem(18)};
      height: ${pxtorem(18)};
      margin-right: 15px;
      &:hover{
          background: ${colors.primaryMain};
      }
  }

 /* table filter dropwdown style */
  .filterDropdown {
    .MuiPaper-root {
      padding: 15px 10px 3px 10px !important;
    }
    .MuiList-root {
      padding-top: 0;
      .MuiInputBase-input {
        padding-right: 35px;
      }
      .searchIcon {
        position: absolute;
        right: 8px;
        top: 8px;
        fill: rgba(0, 0, 0, 0.56);
      }
    } 
    .listWrapper .MuiFormGroup-root{
      max-height: 250px;
      flex-wrap: nowrap;
      ${customScroll};
      overflow: auto;
    }
    .MuiInputLabel-root {
      top: 0;
    }
    .MuiFormControlLabel-root {
      margin-left: 0;
      margin-right: 0;
      padding-right: 10px;
      max-width: 240px;
      word-break: break-word;
      white-space: normal;
      .MuiCheckbox-root {
        padding: 4px 9px;        
      }
      .MuiTypography-root{
        font-size: ${pxtorem(13)};
        letter-spacing: 0.15px;
        color: ${colors.secondaryContrastText};
      }
    }
  }

  .btn-upload-file {    
    margin-left: 10px;
    .MuiButton-endIcon{
      width: 48px;
      height: 34px;
      border-radius: 0 25px 25px 0;
      margin: 0 -15px 0 15px;
      border-left:solid 1px  ${colors.primaryDark};     
      display: flex;
      align-items: center;
      justify-content: center;
      .MuiSvgIcon-root {
        font-size: ${pxtorem(26)};
      }
      &:hover {
        background: ${colors.secondaryDark};
      }      
    }
    &:hover {
      background: ${colors.secondaryLight};
      .MuiButton-endIcon{ 
        background: ${colors.secondaryDark};
      }
    }
    &.hoverBlue:hover {
      background: ${colors.secondaryDark};
    }
  }
  

  /* file upload style */
  .MuiDialogContent-root {   
    ${customScroll};
    &::-webkit-scrollbar {
      width: 12px;   
    }
    &::-webkit-scrollbar-thumb {
      border-right: solid 5px white;
      border-radius: 0;
    }
  }

  .FileUploadWrapper {   
    .fileUpload {
      background: #f9f9f9;
      border: dashed 1px rgba(196, 196, 196, 1);
      border-radius: 4px;
      padding: 24px 16px;
      text-align: center;
      margin: 0 0 15px 0;
      min-height: 235px;      
      ${hvCenter};
      flex-direction: column;        
    }  
    .iconUpload {
      color:  ${colors.primaryMain};
      margin: 0 0 15px 0;
      font-size: 1.6rem;
    } 
    .lblfilesize, label {
      font-size: ${pxtorem(16)};
      color:  ${colors.primaryMain};
      margin-bottom: 5px;
    }    
  }
  .fileuploadPadding {
    padding-left: 40px;
    ${media.md`
      padding-left: 0;
    `}
  }
  .fileDetailWrapper {
    padding-left: 40px;     
    overflow-y: scroll;
    overflow-x: hidden;
    scroll-behavior: smooth;
    max-height: 245px;     
    width: 100%;
    min-width: 100%;
    border: solid 1px white;
    ${customScroll};    
    display: flex;
    flex-direction: column-reverse;  
    ${media.md`
      padding-left: 0;
    `}
    .fileIcon{
      min-width: 35px;
      .iconUpload {
        width: 18px;
        fill: ${colors.primaryDark};
        margin: 0 0 0 4px;
      }
    }
    .fileAction {    
      .MuiCircularProgress-svg {
        color: ${colors.secondaryDark};
      }
      .iconBtn img{
        width: 18px;
      }
    }
    .fileDetail {
      width: 100%;
      padding-bottom: 10px;      
    }
    .fileUploadList {
      margin-bottom: 8px;
      min-height: 63px;    
      border-bottom: solid 1px ${colors.borderColor};
      &:first-child{
        border-bottom: none;
      }
      .fileName{
        width: 100%;        
      }    
    }
  }
  
  /* Common Modals */
  .digitalModal {
    .MuiDialogTitle-root{
      font-size: ${pxtorem(16)};   
      font-family: ${fontFamily.poppins};
      font-weight: 600;
      line-height: 1.5;
      letter-spacing: 0;
      color: ${colors.primaryMain};
      text-transform: uppercase;
      padding-top: 25px;
      padding-bottom: 25px;
    }
    .MuiDialogContent-root {
      /* border-top: none; */
      border-bottom: none;
      min-height: 60px;
    }
    .MuiDialogActions-root {
      box-shadow: 0px -2px 3px 0px rgba(0, 0, 0, 0.05);
      padding: 20px 25px 25px 25px;
    }
  }

  // error screen wrapper
  .errorWrapper{
    border-radius: 12px;
    padding: 20px;
    max-width: 550px;
    min-width: 550px;
    margin-top: -70px;
    background: ${colors.white};
    position: relative;
    &:after {
      content: '';
      position: absolute;
      width: 150px;
      height: 150px;
      right: -60px;
      bottom: -14px;
      z-index: -1;
      background-image: url(${managePlatformBottomBg});
      background-position: 0 0;
      background-repeat: no-repeat;   
      background-size: contain;     
    }
  }

  .dowloadAttachWrapper {
      min-height: 100px;
      padding: 0 10px;
   }

  // Auto complete dropdown scrll

  .MuiAutocomplete-popper {
    .MuiAutocomplete-listbox {
      ${customScroll}
    }    
  }
  // input disable field
  .MuiOutlinedInput-input.Mui-disabled,
  .MuiInputLabel-root.Mui-disabled
  {
    -webkit-text-fill-color: ${colors.secondaryText};
  }
  
`;


// main container

export const MainWrapperContainer = styled('div')`   
  display: flex;
  height: 100%;
`;

// right container style
export const RightContainerStyled = styled('div')`   
  width: 100%;
  overflow: auto;
  padding: 16px 40px 0 60px;
  ${media.mmd`
    padding: 16px 30px 0 20px;
  `}
  ${media.xxxl`
    max-width: 70%;
  `}
  &.managePlatformContainer {   
    padding: 16px 0 0 0;
    .platform.detail.tab {
      margin-bottom: 10px;
    }
  }
`;


// global data table style